import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { SEOHead } from "@/components/SEOHead";

const Terminos = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Términos y Condiciones de Servicio | CreceLocal"
        description="Términos y condiciones de uso de la plataforma CreceLocal. Leé nuestros acuerdos de servicio, políticas de facturación, cancelaciones y reglas de uso aceptable."
        canonical="/terminos"
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-[800px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert prose-sm sm:prose-base md:prose-lg max-w-none"
          >
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-8 text-foreground">
              Términos y Condiciones
            </h1>

            <p className="text-muted-foreground mb-12">
              <strong>CreceLocal LLC</strong>
              <br />
              Última actualización: julio 2026
            </p>

            <div className="space-y-12 text-muted-foreground">
              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  1. Aceptación de los términos
                </h2>
                <p>
                  Al acceder o utilizar los servicios de CreceLocal LLC
                  ("CreceLocal", "nosotros", "nuestro"), incluyendo el sitio web
                  crecelocal.com y la plataforma de automatización comercial,
                  aceptás estos Términos y Condiciones en su totalidad. Si no
                  estás de acuerdo con alguna parte de estos términos, no podés
                  usar nuestros servicios.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  2. Descripción del servicio
                </h2>
                <p className="mb-4">
                  CreceLocal es una plataforma de software como servicio (SaaS)
                  que provee herramientas de automatización comercial,
                  incluyendo agentes de inteligencia artificial conversacional,
                  CRM, automatizaciones de seguimiento e integración con
                  plataformas de mensajería y publicidad digital.
                </p>
                <p className="mb-4">
                  El servicio se ofrece bajo los siguientes planes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Plan Arrancá:</strong> acceso mensual con soporte
                    grupal
                  </li>
                  <li>
                    <strong>Plan Crecé:</strong> acceso mensual con soporte
                    individual
                  </li>
                  <li>
                    <strong>Plan Escalá:</strong> acceso mensual con agentes de
                    IA ilimitados (disponible como upsell para clientes activos)
                  </li>
                  <li>
                    <strong>Implementación personalizada:</strong> servicio de
                    configuración llave en mano, pago único
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  3. Elegibilidad
                </h2>
                <p className="mb-4">Para usar CreceLocal debés:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Tener al menos 18 años de edad</li>
                  <li>
                    Tener capacidad legal para celebrar contratos vinculantes
                  </li>
                  <li>
                    Usar el servicio exclusivamente para fines comerciales
                    legítimos
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  4. Registro y cuenta
                </h2>
                <p className="mb-4">
                  Al registrarte creás una cuenta bajo tu responsabilidad. Sos
                  responsable de mantener la confidencialidad de tus
                  credenciales y de todas las actividades que ocurran bajo tu
                  cuenta. Debés notificarnos de inmediato ante cualquier uso no
                  autorizado.
                </p>
                <p>
                  CreceLocal se reserva el derecho de suspender o cancelar
                  cuentas que violen estos términos.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  5. Pagos y facturación
                </h2>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Planes de suscripción
                </h3>
                <p>
                  Los planes mensuales se facturan de forma recurrente en la
                  fecha de cada ciclo. El cobro se realiza automáticamente en el
                  método de pago registrado.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Implementación personalizada
                </h3>
                <p>
                  El pago único de implementación se cobra al momento de la
                  contratación y no es reembolsable salvo lo dispuesto en
                  nuestra Política de Reembolso.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Uso de IA (mensajes adicionales)
                </h3>
                <p className="mb-4">
                  Los planes con agentes de inteligencia artificial incluyen una
                  cantidad de mensajes de IA por mes sin costo adicional:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>
                    <strong>Plan Crecé:</strong> 2.000 mensajes de IA incluidos
                    por mes
                  </li>
                  <li>
                    <strong>Plan Escalá:</strong> 6.000 mensajes de IA incluidos
                    por mes
                  </li>
                </ul>
                <p>
                  Superado el límite mensual incluido, cada mensaje adicional
                  enviado por la IA se factura a{" "}
                  <strong>USD 0,0024 por mensaje</strong>. El consumo adicional
                  se cobra junto con la facturación del ciclo correspondiente.
                  El plan Arrancá no incluye agentes de IA.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Campañas de WhatsApp
                </h3>
                <p>
                  El envío de campañas de WhatsApp se factura a{" "}
                  <strong>USD 0,0851 por mensaje de campaña enviado</strong>, en
                  todos los planes. Este costo corresponde a las tarifas de la
                  API de WhatsApp Business (Meta) y puede ajustarse si Meta
                  modifica sus tarifas. El consumo se cobra junto con la
                  facturación del ciclo correspondiente.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
                  Precios
                </h3>
                <p className="mb-4">
                  El precio de tu plan queda fijado al momento de la suscripción
                  y no se modifica mientras la suscripción se mantenga activa.
                  Los aumentos de precio aplican únicamente a suscripciones
                  nuevas: si cancelás y volvés a suscribirte, el precio vigente
                  en ese momento será el que corresponda a tu nueva suscripción.
                </p>
                <p>
                  Las tarifas de consumo variable (mensajes de IA adicionales y
                  campañas de WhatsApp) pueden ajustarse según lo indicado en
                  las secciones anteriores.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  6. Soporte
                </h2>
                <p className="mb-4">
                  Todos los planes incluyen consultas ilimitadas por WhatsApp,
                  de lunes a viernes de 9 a 18 hs (hora Argentina).
                </p>
                <p className="mb-4">Adicionalmente:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>
                    <strong>Plan Crecé:</strong> 1 videollamada personalizada
                    por mes incluida
                  </li>
                  <li>
                    <strong>Plan Escalá:</strong> 2 videollamadas personalizadas
                    por mes incluidas
                  </li>
                </ul>
                <p>
                  Las videollamadas no utilizadas no se acumulan para meses
                  siguientes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  7. Cancelación
                </h2>
                <p>
                  Podés cancelar tu suscripción en cualquier momento desde tu
                  cuenta o contactando a info@crecelocal.com. La cancelación es
                  efectiva al final del ciclo de facturación en curso. No se
                  realizan reembolsos proporcionales por días no utilizados
                  salvo lo establecido en la Política de Reembolso.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  8. Uso aceptable
                </h2>
                <p className="mb-4">Al usar CreceLocal te comprometés a:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    No usar el servicio para enviar comunicaciones no
                    solicitadas (spam) en violación de la legislación aplicable
                  </li>
                  <li>
                    No usar el servicio para actividades ilegales, fraudulentas
                    o que infrinjan derechos de terceros
                  </li>
                  <li>
                    No intentar acceder sin autorización a sistemas, datos o
                    cuentas de otros usuarios
                  </li>
                  <li>
                    Cumplir con las políticas de uso de las plataformas
                    integradas, incluyendo WhatsApp Business API (Meta),
                    Instagram y otras
                  </li>
                </ul>
                <p className="mt-4">
                  El incumplimiento de estas reglas puede resultar en la
                  suspensión inmediata del servicio sin derecho a reembolso.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  9. Propiedad intelectual
                </h2>
                <p className="mb-4">
                  Todo el contenido, código, diseño y materiales de CreceLocal
                  son propiedad de CreceLocal LLC o de sus licenciantes. El uso
                  del servicio no te otorga ningún derecho de propiedad sobre la
                  plataforma.
                </p>
                <p>
                  Vos conservás la propiedad de los datos y contenidos que
                  cargás en la plataforma.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  10. Datos del usuario
                </h2>
                <p>
                  Al usar CreceLocal autorizás el procesamiento de los datos de
                  tus contactos y prospectos según nuestra Política de
                  Privacidad. Sos responsable de obtener los consentimientos
                  necesarios de tus propios clientes para el uso de
                  comunicaciones automatizadas, en particular a través de
                  WhatsApp Business API.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  11. Integraciones de terceros
                </h2>
                <p>
                  CreceLocal se integra con servicios de terceros (Meta, Google,
                  TikTok, WhatsApp Business API, entre otros). No somos
                  responsables por interrupciones, cambios de políticas o fallas
                  en dichos servicios. El uso de estas integraciones está sujeto
                  a los términos de cada plataforma.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  12. Limitación de responsabilidad
                </h2>
                <p className="mb-4">
                  En la máxima medida permitida por la ley aplicable, CreceLocal
                  LLC no será responsable por:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pérdida de ingresos, clientes o datos</li>
                  <li>
                    Daños indirectos, incidentales o consecuentes derivados del
                    uso o imposibilidad de uso del servicio
                  </li>
                  <li>
                    Interrupciones del servicio causadas por terceros, fuerza
                    mayor o mantenimiento
                  </li>
                </ul>
                <p className="mt-4">
                  La responsabilidad total de CreceLocal ante vos no superará el
                  monto pagado en los últimos 3 meses de servicio.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  13. Garantía
                </h2>
                <p className="mb-4">
                  CreceLocal ofrece una garantía de 30 días desde el inicio del
                  servicio. Los términos específicos se detallan en la Política
                  de Reembolso.
                </p>
                <p>
                  El servicio se provee "tal como está". No garantizamos
                  resultados comerciales específicos derivados del uso de la
                  plataforma.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  14. Modificaciones al servicio
                </h2>
                <p>
                  CreceLocal se reserva el derecho de modificar, suspender o
                  discontinuar cualquier parte del servicio con un aviso previo
                  razonable. No seremos responsables ante vos ni ante terceros
                  por dichas modificaciones.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  15. Ley aplicable y jurisdicción
                </h2>
                <p>
                  Estos términos se rigen por las leyes del Estado de Delaware,
                  Estados Unidos, sin consideración de sus principios de
                  conflicto de leyes. Cualquier disputa será sometida a la
                  jurisdicción exclusiva de los tribunales del Estado de
                  Delaware.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  16. Contacto
                </h2>
                <div className="mt-4">
                  <p>CreceLocal LLC</p>
                  <p>131 Continental Dr, Suite 305</p>
                  <p>Newark, DE 19713</p>
                  <p>Estados Unidos</p>
                  <p>info@crecelocal.com</p>
                  <p>crecelocal.com</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terminos;
